import { Box, Flex } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PendingSavesProvider, usePendingSavesRegistry } from "../../../common/input/pendingSaves";
import { EditContractStep, useQueryParams } from "../../../common/useQueryParams";
import { urls } from "../../../urls";
import { EditContractProcessNav } from "./EditContractProcessNav";
import { Details } from "./details/Details";
import { Docs } from "./docs/Docs";
import { Review } from "./review/Review";
import { SaveBeforeLeavingProvider, useSaveBeforeLeavingState } from "./saveBeforeLeaving";
import { useContractState } from "./useContractState";

export const EditContractForm = () => {
  const params = useQueryParams();
  const { contractId } = useParams();
  const [searchParams] = useSearchParams();
  const currentStep = params.getEditContractStep();
  const navigate = useNavigate();
  const contractState = useContractState();

  const [originEvent, setOriginEvent] = useState<string>();

  if (!contractId) throw Error("contractId not defined");

  useEffect(() => {
    const eventId = searchParams.get("originEvent");
    if (eventId) setOriginEvent(eventId);
  }, [searchParams]);

  const editPaths = urls.contracts.edit.go(contractId, originEvent);
  const goToStep = (target: { pathname: string; search: string }) =>
    navigate({ pathname: urls.app.contracts.edit.go(contractId), search: target.search });
  const navigateToDetails = () => goToStep(editPaths.details);
  const navigateToDocuments = () => goToStep(editPaths.documents);
  const navigateToReview = () => goToStep(editPaths.review);
  const pendingSaves = usePendingSavesRegistry();
  const saveBeforeLeaving = useSaveBeforeLeavingState(pendingSaves);
  const navigateToStep = (step: EditContractStep) =>
    saveBeforeLeaving.leave(
      step === "details" ? navigateToDetails : step === "docs" ? navigateToDocuments : navigateToReview,
    );

  const navigateToEvent = useCallback(() => {
    if (!originEvent) return;
    navigate(urls.app.sourcingEvents.view.go(originEvent));
  }, [originEvent, navigate]);

  const navigateAfterPublish = useCallback(() => {
    navigate(urls.app.contracts.view.go(contractState.id));
  }, [contractState.id, navigate]);

  const stepContent = (
    <PendingSavesProvider registry={pendingSaves}>
      <SaveBeforeLeavingProvider value={saveBeforeLeaving}>
      <Box display={currentStep === "details" ? undefined : "none"}>
        <Details nextStep={navigateToDocuments} />
      </Box>
      <Box width="full" maxWidth="full" overflowX="hidden" display={currentStep === "docs" ? undefined : "none"}>
        <Docs previousStep={navigateToDetails} nextStep={navigateToReview} />
      </Box>
      <Box display={currentStep === "review" ? undefined : "none"}>
        <Review previousStep={navigateToDocuments} navigateAfterPublish={navigateAfterPublish} />
      </Box>
      </SaveBeforeLeavingProvider>
    </PendingSavesProvider>
  );

  return (
    <Flex direction="column" width="full" maxWidth="full">
      <EditContractProcessNav
        navigateToStep={navigateToStep}
        isSaving={saveBeforeLeaving.isSaving}
        navigateToEvent={originEvent ? navigateToEvent : undefined}
      />
      <Box width="full" maxWidth="full" overflowX="hidden">
        {stepContent}
      </Box>
    </Flex>
  );
};
